<template>
  <v-dialog
    v-model="menuNoticeDialogOpen"
    :max-width="dialogWidth"
    content-class="menu-notice-dialog"
    scrollable
  >
    <v-card flat class="menu-notice-card">
      <v-card-title class="menu-notice-title">
        {{ menuNoticeTitle }}
      </v-card-title>

      <v-card-text class="menu-notice-body">
        <div class="menu-notice-content">
          <div v-html="menuNoticeHtml"></div>
        </div>
      </v-card-text>

      <v-card-text class="menu-notice-option" v-if="showMenuNoticeSuppressOption">
        <label class="menu-notice-checkbox">
          <input
            v-model="suppressMenuNotice"
            type="checkbox"
            class="menu-notice-checkbox-input"
          >
          <span class="menu-notice-checkbox-label">このお知らせを今日は表示しない</span>
        </label>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { dbglog } from '../common/utils.js';

const MENU_NOTICE_READ_KEY_PREFIX = 'ykh-ges-menu-notice-read:'
const MENU_NOTICE_TITLE = 'お知らせ'
const MENU_NOTICE_HTML_PATH = 'notices/notice_menu.html'

export default {
  name: 'MenuNoticeDialog',
  data: () => ({
    suppressMenuNotice: false,
  }),
  computed: {
    menuNoticeHtml () {
      return this.$store.state.menuNoticeHtml;
    },
    menuNoticeTitle () {
      return MENU_NOTICE_TITLE;
    },
    menuNoticeReadKey () {
      const today = new Date().toLocaleDateString('sv-SE');
      return `${MENU_NOTICE_READ_KEY_PREFIX}${MENU_NOTICE_HTML_PATH}:${today}`;
    },
    userAuthority () {
      return Number(this.$store.state.userInfo?.authority);
    },
    showMenuNoticeSuppressOption () {
      return this.userAuthority === 0;
    },
    menuNoticeDialogOpen: {
      get () {
        return this.$store.state.menuNoticeDialogOpen;
      },
      set (val) {
        if (!val) {
          this.persistMenuNoticeState();
        }
        this.$store.commit('setMenuNoticeDialogOpen', val);
      }
    },
    /**
     * 現在のブレークポイントに応じてダイアログ幅を切り替える。
     *
     * @returns {string} Vuetifyのdialogに渡す幅指定
     */
    dialogWidth () {
      switch (this.$vuetify.breakpoint.name) {
      case 'xl':
      case 'lg':
        return '760px';
      case 'md':
        return '80%';
      default:
        return '92%';
      }
    },
  },
  watch: {
    /**
     * ダイアログを開き直した時は「次回から表示しない」のチェック状態を初期化する。
     *
     * @param {boolean} val ダイアログの開閉状態
     */
    menuNoticeDialogOpen (val) {
      if (val) {
        this.suppressMenuNotice = false;
      }
    },
  },
  created () {
    this.loadMenuNoticeHtml();
  },
  methods: {
    /**
     * 周知本文HTMLを読み込み、取得できない場合は空文字にフォールバックする。
     *
     * @returns {Promise<void>}
     */
    async loadMenuNoticeHtml () {
      try {
        const response = await fetch(`./${MENU_NOTICE_HTML_PATH}`);
        this.$store.commit('setMenuNoticeHtml', response.ok ? await response.text() : '');
      }
      catch (err) {
        dbglog(`MenuNoticeDialog loading menu notice html failed: ${err}`);
        this.$store.commit('setMenuNoticeHtml', '');
      }
    },
    /**
     * authority=0 でチェック済みの場合のみ既読を保存してダイアログを閉じる。
     */
    closeMenuNotice () {
      this.persistMenuNoticeState();
      this.$store.commit('setMenuNoticeDialogOpen', false);
    },
    persistMenuNoticeState () {
      if (this.showMenuNoticeSuppressOption && this.suppressMenuNotice) {
        localStorage.setItem(this.menuNoticeReadKey, '1');
      }
    },
  }
};
</script>

<style lang="scss" scoped>
::v-deep .v-card__title.menu-notice-title {
  background-color: transparent;
  color: #1f3b57;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  font-weight: 900 !important;
  word-break: break-word;
  padding: 20px 24px 8px;
}

.menu-notice-card {
  background-color: #ddeeee !important;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: none !important;
  border: 0 !important;
}

.menu-notice-body {
  line-height: 1.8;
  word-break: break-word;
  background-color: #ddeeee;
  padding: 8px 24px 16px;
}

.menu-notice-content {
  max-height: 55vh;
  overflow-y: auto;
  background-color: #ffffff;
  border: 1px solid #d7d7d7;
  border-radius: 10px;
  padding: 12px;
}

.menu-notice-option {
  display: flex;
  justify-content: center;
  padding: 0 24px 20px;
}

.menu-notice-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 0 auto;
  cursor: pointer;
}

.menu-notice-checkbox-input {
  width: 16px;
  height: 16px;
  margin: 0;
  background-color: #ffffff;
  accent-color: #335566;
  cursor: pointer;
}

.menu-notice-checkbox-label {
  cursor: pointer;
}

::v-deep .menu-notice-dialog {
  border-radius: 8px;
  overflow: hidden;
  background-color: transparent;
  box-shadow: none !important;
}

::v-deep .menu-notice-dialog .v-card {
  border-radius: 8px;
  box-shadow: none !important;
  border: 0 !important;
}
</style>
